import { useState, useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Popup, Toast } from 'react-vant'
import Header from '@/components/Header'
import Back from '@/components/Back'
import FixedBottom from '@/components/FixedBottom'
import CustomButton from '@/components/CustomButton'
import CustomTextInput from '@/components/CustomTextInput'
import CountrySelector from '@/components/CountrySelector'
import VerificationCodeInput from '@/components/VerificationCodeInput'
import { APIWeb } from '@/api'
import { useSendCode } from '@/hooks/useSendCode'
import {
  type KycFormData,
  DocumentType,
  isBasicInfoValid,
  getDocumentTypeOptions,
} from '../types'

interface StepBasicInfoProps {
  formData: KycFormData
  updateFormData: (data: Partial<KycFormData>) => void
  onNext: () => void
  onBack: () => void
}

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

const isChineseRegion = (countryCode: string) => {
  return ['CN', 'TW', 'HK', 'MO'].includes(countryCode?.toUpperCase())
}

export default function StepBasicInfo({ formData, updateFormData, onNext, onBack }: StepBasicInfoProps) {
  const { t } = useTranslation()
  const { sendCode, sendCodeLoading } = useSendCode()

  const [showDocumentTypePicker, setShowDocumentTypePicker] = useState(false)
  const [showVerification, setShowVerification] = useState(false)
  const [kycElementsLoading, setKycElementsLoading] = useState(false)
  const [verifyCodeLoading, setVerifyCodeLoading] = useState(false)

  const canProceed = useMemo(() => isBasicInfoValid(formData), [formData])

  const isChinese = useMemo(() => isChineseRegion(formData.address_country), [formData.address_country])
  const lastNamePlaceholder = useMemo(() => isChinese ? '张' : 'Zhang', [isChinese])
  const firstNamePlaceholder = useMemo(() => isChinese ? '三' : 'San', [isChinese])

  const documentTypeOptions = getDocumentTypeOptions(formData.cardholder_type)

  const getDocumentTypeLabel = (type: DocumentType) => {
    const labels: Record<DocumentType, string> = {
      [DocumentType.IdCard]: t('身份证'),
      [DocumentType.Passport]: t('护照'),
    }
    return labels[type] || ''
  }

  const emailError = useMemo(() => {
    if (!formData.email) return ''
    return EMAIL_REGEX.test(formData.email) ? '' : t('请输入有效的邮箱地址X')
  }, [formData.email, t])

  const handleDocumentTypeSelect = useCallback((type: DocumentType) => {
    if (type !== formData.identity_type) {
      updateFormData({
        identity_type: type,
        identity_number: '',
        idCardFrontUrl: '',
        idCardBackUrl: '',
        idCardHandheldUrl: '',
        passportFrontUrl: '',
        passportHandheldUrl: '',
        kyc_verified: false,
      })
    }
    setShowDocumentTypePicker(false)
  }, [formData.identity_type, updateFormData])

  const handleNext = useCallback(async () => {
    if (!canProceed) return

    if (formData.kyc_verified) {
      onNext()
      return
    }

    try {
      setKycElementsLoading(true)
      await APIWeb.verifyKycElements({
        email: formData.email,
        identity_number: formData.identity_number,
        identity_type: formData.identity_type as unknown as IdentityType,
      })
      const result = await sendCode({
        account: formData.email,
        scene: 'kyc_verify',
      })
      if (result) {
        setShowVerification(true)
      }
    } catch (error: any) {
      console.error('KYC要素校验失败:', error)
      Toast.fail(error?.message || t('校验失败请重试'))
    } finally {
      setKycElementsLoading(false)
    }
  }, [canProceed, formData.kyc_verified, formData.email, formData.identity_number, formData.identity_type, sendCode, onNext, t])

  const handleVerifyCode = useCallback(async (code: string) => {
    try {
      setVerifyCodeLoading(true)
      const result = await APIWeb.verifyOldAccountCode({
        account: formData.email,
        scene: 'kyc_verify',
        code,
      })
      if (result?.verify_key) {
        updateFormData({ kyc_verified: true })
        setShowVerification(false)
        onNext()
      } else {
        Toast.fail(t('验证失败'))
      }
    } catch (error: any) {
      console.error('邮箱验证码校验失败:', error)
      Toast.fail(error?.message || t('验证失败请重试'))
    } finally {
      setVerifyCodeLoading(false)
    }
  }, [formData.email, updateFormData, onNext, t])

  const handleResendCode = useCallback(async () => {
    await sendCode({
      account: formData.email,
      scene: 'kyc_verify',
    })
  }, [formData.email, sendCode])

  const maskedEmail = useMemo(() => {
    if (!formData.email) return ''
    const [local, domain] = formData.email.split('@')
    if (!domain) return formData.email
    const masked = local.length > 2
      ? local.slice(0, 2) + '****'
      : '****'
    return `${masked}@${domain}`
  }, [formData.email])

  return (
    <div className="flex flex-col h-full">
      <Header
        leftChildren={<Back onClick={onBack} />}
      />

      <div className="flex-1 px-5 pt-4 pb-24 overflow-y-auto">
        <div className="text-7 font-medium text-[#1D2129] mb-2">
          {t('持卡人信息')}
        </div>
        <p className="text-sm text-[#9BA0AF] mb-6">
          {t('所填信息须与官方签发证件信息一致')}
        </p>

        <div className="space-y-4">
          {/* 国家/地区 */}
          <div>
            <div className="block text-sm text-[#667085] mb-2 font-medium">
              {t('国家或地区')}
            </div>
            <CountrySelector
              value={formData.address_country}
              onChange={(value) => updateFormData({ address_country: value })}
              placeholder={t('选择国家或地区X')}
              returnType="value"
            />
          </div>

          {/* 姓氏 */}
          <div>
            <div className="block text-sm text-[#667085] mb-2 font-medium">
              {t('姓氏')}
            </div>
            <CustomTextInput
              value={formData.last_name}
              onChangeText={(val) => updateFormData({ last_name: val })}
              placeholder={lastNamePlaceholder}
            />
          </div>

          {/* 名字 */}
          <div>
            <div className="block text-sm text-[#667085] mb-2 font-medium">
              {t('名字')}
            </div>
            <CustomTextInput
              value={formData.first_name}
              onChangeText={(val) => updateFormData({ first_name: val })}
              placeholder={firstNamePlaceholder}
            />
          </div>

          {/* 证件类型 */}
          <div>
            <div className="block text-sm text-[#667085] mb-2 font-medium">
              {t('证件类型')}
            </div>
            <div
              onClick={() => setShowDocumentTypePicker(true)}
              className="w-full h-12 px-4 rounded-2.5 border border-solid border-[#EAECF0] bg-white flex items-center justify-between cursor-pointer"
            >
              <span className={formData.identity_type ? 'text-[#1D2129]' : 'text-[#C9CDD4]/70'}>
                {getDocumentTypeLabel(formData.identity_type) || t('选择证件类型')}
              </span>
              <img src="/icon/select-down.svg" alt="" className="w-3" />
            </div>
          </div>

          {/* 证件号 */}
          <div>
            <div className="block text-sm text-[#667085] mb-2 font-medium">
              {t('证件号')}
            </div>
            <CustomTextInput
              value={formData.identity_number}
              onChangeText={(val) => updateFormData({ identity_number: val, kyc_verified: false })}
              placeholder={t('填写证件号码')}
              maxLength={25}
            />
          </div>

          {/* 电子邮箱 */}
          <div>
            <div className="block text-sm text-[#667085] mb-2 font-medium">
              {t('电子邮箱')}
            </div>
            <CustomTextInput
              value={formData.email}
              onChangeText={(val) => updateFormData({ email: val, kyc_verified: false })}
              placeholder={t('输入电子邮箱')}
              inputMode="email"
              isErrored={!!emailError}
            />
            {emailError && (
              <div className="text-xs text-red-500 mt-1">{emailError}</div>
            )}
          </div>
        </div>
      </div>

      <FixedBottom className="bg-white">
        <CustomButton
          onClick={handleNext}
          disabled={!canProceed}
          loading={kycElementsLoading || sendCodeLoading}
        >
          {t('下一步')}
        </CustomButton>
      </FixedBottom>

      {/* 证件类型选择 Popup */}
      <Popup
        visible={showDocumentTypePicker}
        position="bottom"
        round
        closeable
        closeIcon={<img src="/icon/close.svg" alt="" />}
        closeIconPosition="top-right"
        onClose={() => setShowDocumentTypePicker(false)}
      >
        <div className="px-5 pt-5 pb-8">
          <div className="flex items-center justify-between mb-6">
            <div className="text-lg font-medium text-[#1D2129]">
              {t('证件类型')}
            </div>
          </div>

          <div className="flex flex-col gap-3">
            {documentTypeOptions.map((option) => {
              const isSelected = formData.identity_type === option.type
              return (
                <div
                  key={option.type}
                  onClick={() => option.enabled && handleDocumentTypeSelect(option.type)}
                  className={`
                    flex items-center justify-between py-4 transition-all
                    ${option.enabled ? 'cursor-pointer' : 'cursor-not-allowed opacity-40'}
                  `}
                >
                  <span className={`text-base ${option.enabled ? 'text-[#1D2129]' : 'text-[#C9CDD4]'} font-medium`}>
                    {getDocumentTypeLabel(option.type)}
                    {!option.enabled && (
                      <span className="text-sm font-normal ml-1">({t('暂不支持')})</span>
                    )}
                  </span>
                  {isSelected && option.enabled && (
                    <div className="w-6 h-6 rounded-full border-2 border-solid border-[var(--primary-color)] bg-[var(--primary-color)] flex items-center justify-center">
                      <img src="/images/my/icons/select-kyc-type.svg" alt="" />
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        </div>
      </Popup>

      {/* 邮箱验证码 Popup */}
      <Popup
        visible={showVerification}
        position="bottom"
        round
        closeable
        onClose={() => setShowVerification(false)}
        closeIcon={<img src="/icon/close.svg" alt="" />}
        closeIconPosition="top-right"
        style={{ maxHeight: '80vh' }}
      >
        <div className="px-5 pt-5 pb-8">
          <div className="flex items-center justify-between mb-4">
            <div className="text-lg font-medium text-[#1D2129]">
              {t('邮箱验证')}
            </div>
          </div>

          <div className="flex justify-center mt-6 mb-2">
            <img src="/icon/kyc-ver-email-icon.svg" alt="" className="w-20 h-20" />
          </div>

          <VerificationCodeInput
            showTitle={false}
            align="center"
            account={maskedEmail}
            onSubmit={handleVerifyCode}
            onResendCode={handleResendCode}
            resendLoading={sendCodeLoading}
            submitLoading={verifyCodeLoading}
            submitButtonText={t('确认')}
          />
        </div>
      </Popup>
    </div>
  )
}
